import { parse, addMonths, subDays, format } from "date-fns";

var config;
if (process.env.REACT_APP_ENV === "DEV") {
  config = {
    SUPPORT_BASEURL: "http://localhost:8080",
    SUPPORT_ENDPOINT: "http://localhost:8080/api-support/",
    EVENT_BASEURL: "http://localhost:3002",
    EVENT_ENDPOINT: "http://localhost:3002/api-event/",
    AI_CHECKIN_BASEURL: "https://ai-recp.run.place",
    BASEURL: "http://localhost:3001/",
    ENDPOINT: "http://localhost:3001/api/",
    CLIENT_ID: "1008109711902-ohks0soll40laf4u7os9e004nrinn6hc.apps.googleusercontent.com",
  }
} else {
  config = {
    SUPPORT_BASEURL: "https://cuancuan.point-star.com",
    SUPPORT_ENDPOINT: "https://cuancuan.point-star.com/api-support/",
    EVENT_BASEURL: "https://events.pointstar.co.id",
    EVENT_ENDPOINT: "https://cuancuan.point-star.com/api-event/",
    AI_CHECKIN_BASEURL: "https://ai-recp.run.place",
    BASEURL: "https://cuancuan.point-star.com/",
    ENDPOINT: "https://cuancuan.point-star.com/api/",
    CLIENT_ID: "1008109711902-ohks0soll40laf4u7os9e004nrinn6hc.apps.googleusercontent.com",
  }
}

const randomNumber = () => {
  let chars = '1234567890';
  let result = '';
  for (let i = 0; i < 4; i++) {
    result += chars[Math.floor(Math.random() * chars.length)];
  }
  return result;
}

const encodeBase64Payload = (data) => {
  const stringPayload = typeof data === 'string' ? data : JSON.stringify(data);
  return btoa(
    encodeURIComponent(stringPayload).replace(/%([0-9A-F]{2})/g, function toSolidBytes(match, p1) {
      return String.fromCharCode('0x' + p1);
    })
  );
};

const encodeBase64RequestBody = (data) => {
  return JSON.stringify({ data: encodeBase64Payload(data) });
};

const priceFormat = (value, currency = '', isEditing = false) => {
  if (!value) return 0;

  const stringValue = typeof value === 'number' ? String(value) : value;
  if (typeof stringValue !== 'string') return 0;

  try {
    if (isEditing) {
      return stringValue;
    }

    const cleanValue = stringValue.replace(/,/g, '');
    const roundedValue = Number(cleanValue);
    if (Number.isNaN(roundedValue)) return 0;

    const fixedValue = roundedValue.toFixed(3);
    const [integerPart, decimalPart] = fixedValue.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/^0+(?=\d)/, '');

    let formattedValue = formattedInteger;
    if (decimalPart) {
      const trimmedDecimal = decimalPart.replace(/0+$/, '');
      if (trimmedDecimal) {
        formattedValue = `${formattedInteger}.${trimmedDecimal}`;
      }
    }

    return currency ? `${formattedValue} ${currency}` : formattedValue;
  } catch (error) {
    console.error('Price formatting error:', error);
    return 0;
  }
};

const numberFormat = (value) => {
  if (!value) return '';
  value = value.toString();
  const cleanedValue = value.replace(/[^0-9.-]/g, '');
  const parts = cleanedValue.split('.');
  if (parts.length > 2) {
    return parseFloat(parts[0] + '.' + parts.slice(1).join(''));
  }
  if (parts.length === 2) {
    parts[1] = parts[1].slice(0, 3);
    return parseFloat(parts.join('.'));
  }
  return parseFloat(cleanedValue);
};

const nameFormat = (name) => {
  let cleanedName = name;
  if (!cleanedName) {
    return '';
  }
  const allowedPattern = /[a-zA-Z0-9\s'.\-&(),\/]/g;
  cleanedName = cleanedName.replace(/[^a-zA-Z0-9\s'.\-&(),\/]/g, '');
  const maxLength = 100;
  if (cleanedName.length > maxLength) {
    cleanedName = cleanedName.substring(0, maxLength);
  }
  return cleanedName;
}

const slugFormat = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const shortenUrl = (url, maxLength = 30) => {
  if (url.length <= maxLength) {
    return url;
  }
  const ellipsis = '...';
  const ellipsisLength = ellipsis.length;
  if (maxLength < ellipsisLength + 8) {
    throw new Error('maxLength is too small to create a meaningful shortened URL');
  }
  const remainingLength = maxLength - ellipsisLength;
  const startLength = Math.floor(remainingLength * 0.6);
  const endLength = remainingLength - startLength;
  const start = url.substring(0, startLength);
  const end = url.substring(url.length - endLength);
  return `${start}${ellipsis}${end}`;
}

function newOrderTerms(data) {
  const {
    effective_date,
    payment_type,
    product_contract
  } = data;

  const installmentDetails = {
    'Monthly': { monthsPerTerm: 1 },
    'Bimonthly': { monthsPerTerm: 2 },
    'Quarterly': { monthsPerTerm: 3 },
    'Trimesterly': { monthsPerTerm: 4 },
    'Semesterly': { monthsPerTerm: 6 },
    'Annually': { monthsPerTerm: 12 },
    'One Time': { monthsPerTerm: product_contract },
  };

  const detail = installmentDetails[payment_type];
  if (!detail) {
    return [];
  }

  const { monthsPerTerm } = detail;
  const totalTerms = payment_type === 'One Time' ? 1 : Math.ceil(product_contract / monthsPerTerm);

  const startDate = parse(effective_date, "yyyy-MM-dd HH:mm:ss", new Date());
  let currentStartDate = startDate;

  const terms = [];
  let monthsRemaining = product_contract;

  for (let i = 0; i < totalTerms; i++) {
    const monthsForThisTerm = Math.min(monthsPerTerm, monthsRemaining);
    const termEndDate = subDays(addMonths(currentStartDate, monthsForThisTerm), 1);

    terms.push({
      "term_number": i + 1,
      "term_start": format(currentStartDate, "yyyy-MM-dd"),
      "term_end": format(termEndDate, "yyyy-MM-dd"),
      "term_months": monthsForThisTerm
    });

    currentStartDate = addMonths(currentStartDate, monthsForThisTerm);
    monthsRemaining -= monthsForThisTerm;
  }

  return terms;
};

function additionalTerms(data) {
  const {
    effective_date,
    additional_date,
    product_contract,
    payment_type,
  } = data;

  const installmentDetails = {
    'Monthly': { monthsPerTerm: 1 },
    'Bimonthly': { monthsPerTerm: 2 },
    'Quarterly': { monthsPerTerm: 3 },
    'Trimesterly': { monthsPerTerm: 4 },
    'Semesterly': { monthsPerTerm: 6 },
    'Annually': { monthsPerTerm: 12 },
    'One Time': { monthsPerTerm: product_contract },
  };

  const detail = installmentDetails[payment_type];
  if (!detail) {
    return [];
  }

  const { monthsPerTerm } = detail;
  const totalTerms = payment_type === 'One Time' ? 1 : Math.ceil(product_contract / monthsPerTerm);

  const originalStartDate = parse(effective_date, "yyyy-MM-dd HH:mm:ss", new Date());
  const additionalDate = parse(additional_date, "yyyy-MM-dd HH:mm:ss", new Date());

  const getProratedMonths = (proratedStartDate, termEndDate) => {
    let months = (termEndDate.getFullYear() - proratedStartDate.getFullYear()) * 12 + (termEndDate.getMonth() - proratedStartDate.getMonth());
    if (termEndDate.getDate() >= proratedStartDate.getDate()) {
      months++;
    }
    return Math.max(0, months);
  };

  const terms = [];
  let currentStartDate = originalStartDate;
  let monthsRemaining = product_contract;

  for (let i = 0; i < totalTerms; i++) {
    const monthsForThisTerm = Math.min(monthsPerTerm, monthsRemaining);
    const termEndDate = subDays(addMonths(currentStartDate, monthsForThisTerm), 1);

    if (additionalDate > termEndDate) {
      currentStartDate = addMonths(currentStartDate, monthsForThisTerm);
      monthsRemaining -= monthsForThisTerm;
      continue;
    }

    const isFirstProratedTerm = terms.length === 0;
    const effectiveStartDate = isFirstProratedTerm ? additionalDate : currentStartDate;

    if (effectiveStartDate > termEndDate) {
      currentStartDate = addMonths(currentStartDate, monthsForThisTerm);
      monthsRemaining -= monthsForThisTerm;
      continue;
    }

    const termMonths = isFirstProratedTerm
      ? getProratedMonths(effectiveStartDate, termEndDate)
      : monthsForThisTerm;

    terms.push({
      "term_number": terms.length + 1,
      "term_start": format(effectiveStartDate, "yyyy-MM-dd"),
      "term_end": format(termEndDate, "yyyy-MM-dd"),
      "term_months": termMonths
    });

    currentStartDate = addMonths(currentStartDate, monthsForThisTerm);
    monthsRemaining -= monthsForThisTerm;
  }

  return terms;
};

export {
  config,
  randomNumber,
  encodeBase64Payload,
  encodeBase64RequestBody,
  priceFormat,
  numberFormat,
  nameFormat,
  slugFormat,
  shortenUrl,
  newOrderTerms,
  additionalTerms,
};
